import { Component, signal, inject, OnInit } from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ApiService } from './api.service';
import { TranslatePipe } from './i18n.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [FormsModule, CommonModule, RouterModule, TranslatePipe],
  template: `
    <div class="min-h-screen flex items-center justify-center p-6 lg:p-8 font-sans" style="background:#f1f9fe;">
      <div class="w-full max-w-md">
        
        <div class="flex items-center justify-center mb-8">
          <div class="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style="background:linear-gradient(135deg, #1A6FA6, #0C3A5A);">
            <i class="ti ti-shield-lock text-white text-2xl"></i>
          </div>
        </div>

        <div class="bg-white rounded-3xl p-8 shadow-sm relative" style="border:1px solid #EEF2F7;">
          <h1 class="text-2xl font-bold text-slate-900 mb-1">{{ 'resetPassword' | t }}</h1>
          <p class="text-slate-500 text-sm mb-6">Choose a new secure password for your account.</p>

          @if (errorMsg()) {
            <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#FEF2F2; color:#EF4444; border:1px solid #FECACA;">
              <i class="ti ti-alert-circle text-[18px] shrink-0"></i>{{ errorMsg() }}
            </div>
          }

          @if (successMsg()) {
            <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#ECFDF5; color:#059669; border:1px solid #A7F3D0;">
              <i class="ti ti-circle-check text-[18px] shrink-0"></i>{{ successMsg() }}
            </div>
          }

          @if (!successMsg()) {
            <form (ngSubmit)="onSubmit()" class="space-y-5">
              <div>
                <label class="block text-sm font-semibold text-slate-700 mb-1.5">{{ 'newPassword' | t }}</label>
                <input type="password" [(ngModel)]="password" name="password" placeholder="••••••••" 
                       class="w-full h-12 px-4 rounded-2xl text-sm border outline-none transition-all" 
                       style="border-color:#E2E8F0; background:#FAFBFC;"
                       onfocus="this.style.borderColor='#2B9AD2';">
              </div>
              <div>
                <label class="block text-sm font-semibold text-slate-700 mb-1.5">{{ 'confirm' | t }} {{ 'password' | t }}</label>
                <input type="password" [(ngModel)]="confirmPassword" name="confirmPassword" placeholder="••••••••" 
                       class="w-full h-12 px-4 rounded-2xl text-sm border outline-none transition-all" 
                       style="border-color:#E2E8F0; background:#FAFBFC;"
                       onfocus="this.style.borderColor='#2B9AD2';">
              </div>
              <button type="submit" [disabled]="isLoading() || !token" class="w-full h-12 rounded-2xl font-bold text-white shadow-sm hover:opacity-90 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer" 
                      style="background:linear-gradient(135deg, #2B9AD2, #1A6FA6);">
                @if (isLoading()) { <i class="ti ti-loader animate-spin mr-2"></i> {{ 'loading' | t }} }
                @else { {{ 'updatePassword' | t }} }
              </button>
            </form>
          }

          <div class="mt-8 text-center">
            <a routerLink="/login" class="text-sm font-bold text-[#2B9AD2] hover:underline">{{ 'backToLogin' | t }}</a>
          </div>
        </div>

        <p class="text-center text-xs text-slate-400 mt-8 font-medium italic">
          NZ Plumbing Field Services • Security Reset
        </p>
      </div>
    </div>
  `
})
export class ResetPasswordComponent implements OnInit {
  token = '';
  password = '';
  confirmPassword = '';
  isLoading = signal(false);
  errorMsg = signal('');
  successMsg = signal('');

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private api = inject(ApiService);

  ngOnInit() {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    if (!this.token) {
      this.errorMsg.set('Invalid link. Token is missing.');
    }
  }

  onSubmit() {
    if (!this.password) {
      this.errorMsg.set('Please enter a new password.');
      return;
    }
    if (this.password.length < 6) {
      this.errorMsg.set('Password must be at least 6 characters.');
      return;
    }
    if (this.password !== this.confirmPassword) {
      this.errorMsg.set('Passwords do not match.');
      return;
    }

    this.isLoading.set(true);
    this.errorMsg.set('');

    this.api.submitResetPassword({ token: this.token, password: this.password }).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) {
          this.successMsg.set('Password reset successfully! You can now log in.');
          setTimeout(() => this.router.navigate(['/login']), 2000);
        } else {
          this.errorMsg.set(res.message || 'Reset failed.');
        }
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMsg.set('An error occurred. Please try again.');
      }
    });
  }
}
